import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { openDefaultEditor, processDefaultImage } from '@pqina/pintura';
import '@pqina/pintura/pintura.css';
import { toast } from "react-toastify";
import { friendlyFileIcon } from '../../../constants/FriendlyFile';
import { ArrowUpTrayIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DismissibleTag, IconButton, manageInputCls } from '../ManageUi';


function ImageDropper({ images, loadImages = false, aspectRatio = '1 / 1', imageCropAspectRatio = 1 / 1, imageWriter = {
    mimeType: 'image/jpeg',
    targetSize: {
        width: 1600,
        height: 1600,
        upscale: false,
        fit: 'contain',
    },
},
    captions = true,
    file_details = false, msg = 'Supported file types: .jpg, .jpeg, .png, .gif, .webp, .avif',
    editorOnClose = () => {
        console.log('editorOnClose called');
    },
    editorOnProcess = () => {
        console.log('editorOnProcess called');
    },
    editorOnUpload = () => {
        console.log('editorOnUpload called');
    }, }) {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [doneOnce, setDoneOnce] = useState(false);
    const [files, setFiles] = useState([]);

    useEffect(() => {
        if (images && images.length > 0 && !doneOnce) {
            setDoneOnce(true);
            setUploadedFiles(images);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intent: first-images bootstrap only; doneOnce guard prevents re-run after the initial seed and adding it as a dep would refire when the guard flips
    }, [images]);

    const editImage = (image, done, statusMessage = "") => {
        const imageFile = image.pintura ? image.pintura.file : image;
        const imageState = image.pintura ? image.pintura.data : {};
        const editor = openDefaultEditor({
            src: imageFile,
            imageState,
            imageCropAspectRatio: imageCropAspectRatio,
            cropSelectPresetOptions: [
                [1, 'Square'],
            ],
            resizeMaxSize: {
                width: 256,
                height: 256,
            },
            utils: [
                'crop',
                'filter',
                'finetune',
                'annotate',
                'decorate',
                'sticker',
                'frame',
                'redact',
            ],
            imageWriter: imageWriter,

        });
        editor.on('close', () => {
            editorOnClose();
        });
        editor.on('loadabort', (error) => {
            toast.error(`Error: ${error?.error?.message}`, 'alert-error');
            editor.close();
        }
        );
        editor.on('processabort', (error) => {
            toast.error(`Error: ${error?.error?.message}`, 'alert-error');
            editor.close();
        }
        );
        editor.on('loaderror', (error) => {
            toast.error(`Error: ${error?.error?.message}`, 'alert-error');
            editor.close();
        }
        );
        editor.on('processerror', (error) => {
            toast.error(`Error: ${error?.error?.message}`, 'alert-error');
            editor.close();
        }
        );
        editor.on('process', ({ src, dest, imageState }) => {
            let tmpFiles = [...uploadedFiles];
            const preview = new Image();
            preview.src = URL.createObjectURL(dest);

            if (src.preview) URL.revokeObjectURL(src.preview);
            if (dest)
                Object.assign(dest, {
                    preview: URL.createObjectURL(dest),
                });
            tmpFiles.push(dest);
            setUploadedFiles([...uploadedFiles, dest]);
            done(dest);

        });
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/*': [],
        },
        onDrop: async (acceptedFiles) => {
            let tmpFiles = [...uploadedFiles];
            console.log('tmpFiles (before process)', tmpFiles);
            let imageCount = acceptedFiles.filter((file) => file.type && file.type.startsWith('image/')).length;
            await acceptedFiles.forEach(async (file, index) => {
                //if file is an image
                if (file.type && file.type.startsWith('image/') && loadImages) {
                    //pass in index to edit image
                    await editImage(file, (output) => {
                        if (file.preview) URL.revokeObjectURL(file.preview);
                        if (output)
                            Object.assign(output, {
                                preview: URL.createObjectURL(output),
                            });
                        tmpFiles.push(output);
                        setUploadedFiles([...tmpFiles])
                    }, 'Image Upload ' + (imageCount - index) + ' of ' + imageCount);
                } else if (file.type && file.type.startsWith('image/')) {
                    console.log('Processing image file:', file, imageCropAspectRatio);
                    processDefaultImage(file, {
                        imageCropAspectRatio: imageCropAspectRatio,
                        imageWriter: imageWriter,
                    }).then(({ dest, imageState }) => {
                        // Show resulting image preview
                        console.log('Image processed:', dest, imageState);
                        const preview = new Image();
                        preview.src = URL.createObjectURL(dest);
                        // document.body.appendChild(preview);
                        if (file.preview) URL.revokeObjectURL(file.preview);
                        if (dest)
                            Object.assign(dest, {
                                preview: URL.createObjectURL(dest),
                            });
                        tmpFiles.push(dest);
                        console.log('tmpFiles', tmpFiles);
                        setUploadedFiles([...tmpFiles]);
                    });
                } else {
                    console.log('Processing image file:', file);
                    tmpFiles.push(file)
                    setUploadedFiles([...tmpFiles]);
                }
            });
        },
        onDropRejected: (fileRejections, e) => {
            // console.log('fileRejections', fileRejections);
            let msgBody = "<div className='h6'>The following problems were found with your files:</div><ul class='list-disc pl-5'>";
            fileRejections.forEach(({ file, errors }) => {
                msgBody += `<li>${file.name} - `;
                errors.forEach((error) => {
                    msgBody += `<span className='text-red-500'>${error.message}</span>`;
                });
                msgBody += '</li>';
            });
            msgBody += "</ul>";
            toast.error(msgBody, 'alert-error')
            // Handle file rejection
            // You can show a message to the user or log the error
        },
        onError: (fileRejections) => {
            console.error('File Rejections:', fileRejections);
            // Handle file rejections here, e.g., show an error message
        }
    });


    useEffect(
        () => () => {
            // Make sure to revoke the Object URL to avoid memory leaks
            files.forEach((file) => URL.revokeObjectURL(file.preview));
        },
        [files]
    );

    const addCaption = (e, index) => {
        e.preventDefault();
        // console.log(formData);
        let sanitizedInput = uploadedFiles[index]?.captionInput?.trim().toUpperCase();
        let tmpUpdatedFiles = [...uploadedFiles];
        tmpUpdatedFiles[index].caption = tmpUpdatedFiles[index].caption || [];
        if (!sanitizedInput) {
            toast.error('Caption cannot be empty.', 'alert-error');
            return;
        }
        if (tmpUpdatedFiles[index].caption.includes(sanitizedInput)) {
            toast.error('Caption already exists.', 'alert-error');
            return;
        }
        // Add the caption to the file's caption array
        tmpUpdatedFiles[index].caption.push(sanitizedInput);

        tmpUpdatedFiles[index].captionInput = ''; // Clear the input field after adding the caption

        setUploadedFiles(tmpUpdatedFiles || []);
    }

    let thumbs = uploadedFiles.map((file, index) => (<div key={index}>
        <hr className="my-2" />
        <div className="flex gap-3 justify-center items-start" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: '1.25rem' }} >
            {/* <img className="w-[120px] pb-3" src={file?.preview} alt="" /> */}
            {(!file.type || file.type.includes('image')) &&
                <div className="flex-none w-[120px]" style={{ flex: 'none', width: '120px' }}>
                    {/* <Dialog>
                        <Dialog.Trigger as="img" className="w-[120px] rounded-lg border-transparent border mb-3" src={file?.preview} alt=""></Dialog.Trigger>
                        <Dialog.Overlay className="bg-gray-900/95 dark:bg-gray-100/95" />
                        <Dialog.Content className="bg-transparent p-0 m-0 border-transparent">
                            <div>
                                <img src={file?.preview} alt="" />
                            </div>
                        </Dialog.Content>
                    </Dialog> */}
                    {/* <PButtonPure className="w-[120px] rounded-lg border-transparent border mb-3" onClick={() => {
                        setModalOpen(true);
                        handleGalleryImageRead(file);
                    }}> */}
                    <img className="w-full h-full object-cover" src={file?.preview} alt="" style={{ width: '120px', borderRadius: '8px' }} />
                    {/* </PButtonPure> */}
                </div>
            }
            {(file.type && !file.type.includes('image')) &&
                <div className="flex-none w-[120px]">
                    {friendlyFileIcon(file.type)}
                </div>
            }
            {captions &&
                    <div className="flex-1" style={{ flex: '1' }}>
                    <form className="flex items-center gap-2" onSubmit={(e) => addCaption(e, index)}>
                            <input
                                type="text"
                                name="captionInput"
                                className={`${manageInputCls} !mt-0`}
                                value={uploadedFiles[index]?.captionInput}
                                placeholder="Add Tag"
                                onChange={(e) => {
                                    let tmpUpdatedFiles = [...uploadedFiles];
                                    tmpUpdatedFiles[index].captionInput = e.target.value;
                                    setUploadedFiles(tmpUpdatedFiles || []);
                                }}
                            />
                            <button
                                type="submit"
                                className="inline-flex items-center justify-center rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
                            >
                                Add
                            </button>
                    </form>
                    <div className='py-1'></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                        {uploadedFiles[index]?.caption && uploadedFiles[index]?.caption?.length > 0 && (
                            uploadedFiles[index].caption.map((caption, captionIndex) => (
                                <DismissibleTag key={captionIndex} onClick={() => {
                                    console.log("onDismiss triggered");
                                    let tmpUpdatedFiles = [...uploadedFiles];
                                    tmpUpdatedFiles[index].caption.splice(captionIndex, 1);
                                    setUploadedFiles(tmpUpdatedFiles || []);
                                    toast.success('Caption removed successfully.', 'alert-success');
                                }}
                                >
                                    {caption}
                                </DismissibleTag>
                            ))
                        )}
                    </div>
                </div>
            }
            {file_details &&
                <div className="flex-1">
                    <div>{file?.type}</div>
                    <div>{file?.size ? (file.size / 1024).toFixed(2) + ' KB' : ''}</div>
                </div>
            }
            <div className="flex-none flex items-center justify-end" style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1.25rem' }}>
                {(!file.type || file.type.includes('image')) &&
                    <IconButton
                        label="Edit file"
                        icon={PencilSquareIcon}
                        onClick={() =>
                            editImage(file, (output) => {
                                let tmpUpdatedFiles = [...uploadedFiles];
                                tmpUpdatedFiles[index] = output;
                                setUploadedFiles(tmpUpdatedFiles || []);
                                if (file.preview) URL.revokeObjectURL(file.preview);
                                Object.assign(output, {
                                    preview: URL.createObjectURL(output),
                                });
                            })
                        }
                    />
                }
                <IconButton
                    label="Remove file"
                    icon={TrashIcon}
                    className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                    onClick={() => {
                        let tmpUpdatedFiles = [...uploadedFiles];
                        tmpUpdatedFiles.splice(index, 1);
                        setUploadedFiles(tmpUpdatedFiles || []);
                        if (file.preview) URL.revokeObjectURL(file.preview);
                        toast.success('Image removed successfully.', 'alert-success');
                    }
                    }
                />
            </div>
        </div>
        {uploadedFiles.length - 1 === index &&
            <hr className="my-2" />
        }

    </div>
    ));


    return (
        <section className="w-full">
            <div {...getRootProps({ className: 'dropzone mb-4' })}>
                <input {...getInputProps()} />
                <div className="flex gap-3 items-center justify-center cursor-pointer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem', cursor: 'pointer' }}>
                    <ArrowUpTrayIcon aria-hidden="true" className="size-6 text-zinc-500 dark:text-zinc-400" />
                    <div className="flex-none">
                        <div className="p3 text-gray-400 dark:text-gray-700">Drag & drop or click to choose files</div>
                        <div>{msg}</div>
                        <div></div>
                    </div>
                </div>
            </div>
            {uploadedFiles.length > 0 && <>
                <label>Pending Uploads:</label>
                <div>{thumbs}</div>
                <div className="text-center">
                    <div className="h6 text-alert_red py-3">Please upload files before closing the editor.</div>
                    <button className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100" onClick={async () => {
                        let success = await editorOnUpload(uploadedFiles);
                        if (success) {
                            setUploadedFiles([]);
                            setDoneOnce(false);
                        }
                    }}>Upload</button></div>
            </>}
        </section>
    );
}

export default ImageDropper;
